/**
 * Типовое ядро локалей. Общее для сервера и веба: web/tsconfig.json включает этот каталог.
 *
 * Три вида листьев каталога, и различие между ними форсируется типами, а не дисциплиной:
 *   1. `string`   — простой текст без параметров;
 *   2. `Msg<P>`   — сериализуемое сообщение с параметрами и плюралями. Уезжает клиенту;
 *   3. `MsgFn<P>` — функция локали (склонения, порядок слов). ТОЛЬКО сервер: функцию
 *                   нельзя сериализовать в JSON, поэтому в веб-каталоге она запрещена
 *                   и её появление там ловит assertI18nReady().
 */

/**
 * Локали деплоя. Один деплой = один язык, заданный env `SITE_LANGUAGE`.
 *
 * Сейчас язык один. Механизм мультиязычности сохранён целиком: вторая локаль добавляется
 * папкой в catalogs/ и одним членом этого union, после чего проверка полноты каталогов
 * в boot-assert включается сама. Отказ от зеркала `ru/` — решение владельца продукта,
 * см. memory/i18n-hy-only.md.
 */
export type Locale = 'hy';

/** Параметры подстановки. Только примитивы: каталог обязан сериализоваться в JSON. */
export type Params = Record<string, string | number>;

/** Ветки плюралей. Набор категорий различается по языкам, поэтому все они опциональны. */
export type PluralBranches = Partial<Record<Intl.LDMLPluralRule, string>>;

/**
 * Сообщение с подстановками вида `{name}` и (опционально) плюралями.
 * `p.by` называет параметр, по которому выбирается ветка; внутри ветки `#` заменяется
 * на его значение.
 */
export interface Msg<P extends Params = Params> {
  readonly s: string;
  readonly p?: { readonly by: keyof P & string } & PluralBranches;
  /** Фантомное поле: несёт тип параметров. В рантайме его нет — см. msg(). */
  readonly __p?: P;
}

/** Сообщение, которое нельзя выразить шаблоном. Только сервер. */
export type MsgFn<P extends Params = Params> = (p: P) => string;

/**
 * Расширение литеральных типов до общих.
 *
 * Зачем: эталонный каталог даёт литеральные типы ("Հաստատել", а не string), и вторая
 * локаль, аннотированная как Widen<typeof hy>, обязана иметь ТЕ ЖЕ КЛЮЧИ, но НЕ те же
 * строки. Пропущенный ключ, лишний ключ или потерянный параметр — ошибка сборки.
 * Практическое следствие: `npm run typecheck` и есть полный чеклист переводчика.
 */
export type Widen<T> = T extends string
  ? string
  : T extends Msg<infer P>
    ? Msg<P>
    : T extends (p: infer P) => string
      ? (p: P) => string
      : T extends readonly (infer E)[]
        ? readonly Widen<E>[]
        : { readonly [K in keyof T]: Widen<T[K]> };

/**
 * Каталог после привязки на клиенте: каждое `Msg` превращается в вызываемую функцию,
 * простые строки остаются строками. Функции локали сюда не попадают — их нет в
 * веб-каталоге по построению.
 */
export type Bind<T> = T extends string
  ? string
  : T extends Msg<infer P>
    ? (p: P) => string
    : T extends readonly (infer E)[]
      ? readonly Bind<E>[]
      : { readonly [K in keyof T]: Bind<T[K]> };

/**
 * Конструктор сообщения. Существует ради вывода типа параметров: `msg<{days:number}>(...)`
 * фиксирует, что при форматировании обязателен `days`.
 */
export function msg<P extends Params = Record<never, never>>(
  s: string,
  p?: Msg<P>['p'],
): Msg<P> {
  return p === undefined ? { s } : { s, p };
}

/** Узел каталога — сообщение, а не вложенная секция. */
export function isMsg(node: unknown): node is Msg {
  return typeof node === 'object' && node !== null && 's' in node;
}
